import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FaDownload, FaFileArrowUp } from 'react-icons/fa6';

import { Button } from '../../../components/ui/button';
import { CONTACT_ECOTIC } from '../../../constants/config';
import { FILE_FOLDERS } from '../../../constants/file-types';
import { api } from '../../../core/api/queries';
import { filesApi } from '../../../core/api/files.api';
import { getErrorMessage } from '../../../core/api/response-handler';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import { useConfirmationDialog } from '../../../shared/hooks';
import { ConfirmationDialog } from '../../../shared/components/dialogs/ConfirmationDialog';
import { useEditionDocuments } from '../../../shared/hooks/use-edition-documents';
import { hrefTelefon } from '../../../shared/utils/contact-ecotic';
import { YoutubeFacade } from '../../../shared/components/YoutubeFacade';
import { InParteneriatCu } from '../../../shared/components/Parteneri';
import { toastOneError, toastOneSuccess } from '../../../toast';
import { usersApi } from '../../users/api/users.api';
import { useProtocolDeColaborare } from '../../auth/hooks/useProtocolDeColaborare';
import { mesajStareProtocol } from '../../auth/utils/protocol-descarcare';
import { useLessonsQuery } from '../../lessons/hooks/useLessonsQueries';
import { practicalPaths, stareProvocareText } from '../../lessons/features/practical/practical-evidence';
import {
  anuntProvocare,
  corpInlocuireProtocol,
  motivBlocareInlocuireProtocol,
  protocolulSeArataPeAcasa,
} from '../utils/acasa-continut';

/** Numele persoanei de contact ECOTIC — decizia owner-ului 21.09, care anuleaza „fara nume" din 16.09. */
const PERSOANA_CONTACT = 'Alexandra Udrea';
const TELEFON_HREF = hrefTelefon(CONTACT_ECOTIC.telefon);

const LINK = 'inline-flex min-h-11 items-center underline underline-offset-4';

/** Starea de incarcare a INLOCUIRII protocolului (item 10) — pentru anuntul accesibil de sub butoane. */
type StareInlocuireProtocol = 'inactiv' | 'se-incarca' | 'se-salveaza' | 'succes' | 'eroare';

const MESAJ_STARE_INLOCUIRE: Record<StareInlocuireProtocol, string | null> = {
  inactiv: null,
  'se-incarca': 'Se încarcă fișierul...',
  'se-salveaza': 'Se salvează protocolul...',
  succes: 'Protocolul a fost înlocuit cu succes.',
  eroare: 'A apărut o eroare. Încearcă din nou.',
};

/**
 * «Acasă» pentru PROFESOR — pagina de bun venit din macheta owner-ului (21.09): text centrat, verde, bold.
 *
 * Codurile vin din JWT (`codProfesor` = codul cu care se inscriu elevii, `codSe`), deci fara cerere noua;
 * fiecare rand apare doar daca are valoare. Numele scolii vine din `schools.own`: cat se incarca sau daca
 * pica, al doilea rand al titlului arata doar numele profesorului — fara spinner, fara eroare.
 *
 * Componenta se monteaza DOAR pentru PROFESOR (`AcasaPage`): `useProtocolDeColaborare` cere PDF-ul la montare
 * — doar cu scoala aprobata in editia curenta (poarta din hook) si o singura data, din cache-ul comun.
 */
export default function AcasaProfesor() {
  const { userInfo } = useAuthProviderV2();
  const { NAV_LINKS_V2, PAGES } = useRoutesProvider();
  const { data: scoala } = api.schools.own.useQuery();
  const { stare, descarca, reincearca } = useProtocolDeColaborare();
  const documente = useEditionDocuments();
  const protocolGata = stare?.stare === 'gata';
  // `null` cat se incarca si cand e „gata": atunci butonul spune singur tot ce e de spus.
  const mesajProtocol = stare ? mesajStareProtocol(stare, 'dumneavoastra') : null;

  const numeScoala = scoala?.numeInstitutieInvatamant?.trim();
  const randDoi = [userInfo?.nume?.trim(), numeScoala].filter(Boolean).join(' / ');
  const caleProfil = NAV_LINKS_V2[PAGES.PROFILE]?.PATH;

  // Item 10/11 (21.09): butoanele reale pentru protocol + ascunderea zonei cand editia curenta e
  // deja confirmata. Acelasi `queryKey`, `['profile']`, ca `useProfileWithForm` / pagina de Profil
  // — daca profilul a fost deja incarcat acolo, React Query serveste din cache, fara al doilea request.
  const queryClient = useQueryClient();
  const { data: profil } = useQuery({
    queryKey: ['profile'],
    queryFn: () => usersApi.getProfile(),
  });

  const inputIncarcareRef = useRef<HTMLInputElement>(null);
  const [urlDeInlocuit, setUrlDeInlocuit] = useState<string | null>(null);
  const [stareInlocuire, setStareInlocuire] = useState<StareInlocuireProtocol>('inactiv');
  /** Cauza reala cand inlocuirea nu poate porni (ex. telefon lipsa) — inlocuieste mesajul generic. */
  const [motivBlocare, setMotivBlocare] = useState<string | null>(null);

  const inlocuireProtocolMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => usersApi.updateProfile(payload),
    onSuccess: () => {
      setStareInlocuire('succes');
      toastOneSuccess('Protocolul a fost înlocuit cu succes.');
      // Reface `profil` (deci `protocolulSeArataPeAcasa`) SI orice formular de Profil deja montat.
      void queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (eroare) => {
      setStareInlocuire('eroare');
      toastOneError(getErrorMessage(eroare, 'Actualizarea protocolului a eșuat.'));
    },
  });

  const confirmareInlocuire = useConfirmationDialog({
    onConfirm: () => {
      if (!urlDeInlocuit) return;
      // Corp MINIMAL: backendul face PATCH real, deci campurile netrimise raman neschimbate. Cauzele
      // pe care le-ar respinge oricum (telefon lipsa) s-au verificat deja inainte de upload.
      const payload = corpInlocuireProtocol(profil, urlDeInlocuit);
      setUrlDeInlocuit(null);
      setStareInlocuire('se-salveaza');
      inlocuireProtocolMutation.mutate(payload);
    },
    onCancel: () => setUrlDeInlocuit(null),
  });

  const declanseazaIncarcare = () => {
    // Inainte de upload: altfel profesorul incarca fisierul si abia la salvare afla ca lipseste telefonul.
    const motiv = motivBlocareInlocuireProtocol(profil);
    setMotivBlocare(motiv);
    if (motiv) {
      setStareInlocuire('eroare');
      toastOneError(motiv);
      return;
    }
    inputIncarcareRef.current?.click();
  };

  const onFisierSelectat = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fisier = e.target.files?.[0];
    // Reset imediat, ca acelasi fisier sa poata fi reselectat dupa o anulare din dialog.
    e.target.value = '';
    if (!fisier) return;

    setStareInlocuire('se-incarca');
    try {
      // Exact ca in pagina de Profil (`ProfilePage.tsx` / `useProfileWithForm.handleFileUpload`):
      // `filesApi.uploadFile` isi are propriul toast de incarcare/eroare, deci nu-l dublam aici.
      const url = await filesApi.uploadFile(fisier, FILE_FOLDERS.USER_PROTOCOLS);
      setUrlDeInlocuit(url);
      setStareInlocuire('inactiv');
      confirmareInlocuire.show();
    } catch {
      setStareInlocuire('eroare');
    }
  };

  const protocolVizibil = protocolulSeArataPeAcasa(profil);

  // Anuntul provocarii pentru scoala: din lista de provocari, fara spinner si fara eroare — cat nu
  // exista o provocare vizibila si in termen, blocul pur si simplu nu apare.
  const { data: provocari } = useLessonsQuery('SCHOOL_CHALLENGE');
  const anunt = anuntProvocare(provocari);
  const provocareaAnuntata = anunt ? provocari?.find((p) => p.id === anunt.id) : undefined;
  const stareAnunt = stareProvocareText(
    provocareaAnuntata?.depuneriInAsteptare,
    provocareaAnuntata?.depuneriAprobate
  );
  const inlocuireInCurs = stareInlocuire === 'se-incarca' || stareInlocuire === 'se-salveaza';
  const mesajStareInlocuire = motivBlocare ?? MESAJ_STARE_INLOCUIRE[stareInlocuire];

  return (
    // `pt-14 md:pt-0`: sub `md` hamburgerul flotant (marginea de jos la 68px) acoperea inceputul titlului
    // centrat; 16 (p-4 din pagina) + 56 = 72. Acelasi tipar ca `ListingActions` / `ParentalApprovalPage`.
    <div className="mx-auto max-w-2xl space-y-6 pt-14 text-center font-semibold text-primary md:pt-0">
      <h1 className="font-heading text-2xl">
        Bine ați venit pe platforma Școala Ecoterrienilor,
        {randDoi && (
          <>
            <br />
            <span data-acasa-salut>{randDoi}</span>
          </>
        )}
      </h1>

      {anunt && (
        // Suprafata proprie, text la stanga si greutate normala: e un anunt de citit, nu un rand din
        // coloana centrata. Imaginea e `object-contain` pe alb — vizualul ECOTIC are personaje pana in
        // margini, iar o decupare le-ar taia.
        <section
          data-acasa-provocare
          aria-label="Provocare pentru școala ta"
          className="overflow-hidden rounded-lg border border-lilac bg-card text-left font-normal text-dark"
        >
          {anunt.imagine && (
            <img
              src={anunt.imagine}
              alt=""
              width={1200}
              height={800}
              className="aspect-[3/2] w-full bg-white object-contain"
            />
          )}
          <div className="space-y-2 p-4">
            <p className="text-pretty font-semibold">{anunt.intro}</p>
            <h2 className="font-heading text-xl font-bold text-balance text-primary">{anunt.titlu}</h2>
            <p className="text-pretty">{anunt.detalii}</p>
            {stareAnunt && (
              <p role="status" className="font-medium">
                {stareAnunt}
              </p>
            )}
            <Button asChild className="min-h-11 w-full sm:w-auto">
              <Link to={practicalPaths(anunt.id, 'SCHOOL_CHALLENGE').detalii}>Vezi provocarea</Link>
            </Button>
          </div>
        </section>
      )}

      {(userInfo?.codProfesor || userInfo?.codSe) && (
        <section data-acasa-coduri className="space-y-1">
          {userInfo?.codProfesor && (
            <p>
              Codul unic al școlii dumneavoastră: <strong className="font-bold">{userInfo.codProfesor}</strong>
            </p>
          )}
          {userInfo?.codSe && (
            <p>
              Cod SE: <strong className="font-bold">{userInfo.codSe}</strong>
            </p>
          )}
        </section>
      )}

      {(documente.mecanism || documente.regulament) && (
        <section data-acasa-documente className="space-y-1">
          {documente.mecanism && (
            <p>
              <a href={documente.mecanism} target="_blank" rel="noopener noreferrer" className={`${LINK} font-bold`}>
                Mecanismul proiectului
              </a>
            </p>
          )}
          {documente.regulament && (
            <p>
              <a href={documente.regulament} target="_blank" rel="noopener noreferrer" className={`${LINK} font-bold`}>
                Regulamentul proiectului
              </a>
            </p>
          )}
        </section>
      )}

      {/* Item 10/11 (21.09): butoane reale, in loc de link text; zona dispare cand
          `protocolInEditiaCurenta` e true — profesorul are deja protocolul confirmat pe editia asta,
          si corectiile raman doar in Profil. */}
      {protocolVizibil && (
        <section data-acasa-protocol className="space-y-3">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            {/* Descarcarea cere PDF-ul scolii prin aplicatie, deci e inactiva cat protocolul nu e
                `gata`; motivul apare sub butoane. */}
            <Button
              type="button"
              onClick={descarca}
              disabled={!protocolGata}
              className="w-full sm:w-auto"
            >
              <FaDownload className="mr-2" />
              Descarcă protocolul
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={declanseazaIncarcare}
              disabled={inlocuireInCurs}
              className="w-full sm:w-auto"
            >
              <FaFileArrowUp className="mr-2" />
              {stareInlocuire === 'se-incarca' ? 'Se încarcă...' : 'Încarcă protocolul semnat'}
            </Button>
            <input
              type="file"
              ref={inputIncarcareRef}
              className="hidden"
              onChange={onFisierSelectat}
            />
          </div>

          {/* De ce e stins butonul de descarcare si ce se poate face (2026-10-07): inainte, orice
              stare in afara de „gata" lasa doar un buton inactiv, fara niciun cuvant. Textele vin
              din `mesajStareProtocol`, aceleasi ca in meniu, in registrul paginii. */}
          {mesajProtocol && (
            <div
              data-acasa-protocol-stare={stare?.stare}
              role={mesajProtocol.ton === 'eroare' ? 'alert' : 'status'}
              className={`text-small font-normal ${mesajProtocol.ton === 'eroare' ? 'text-error' : 'text-gray-600'}`}
            >
              <p className="text-pretty">
                {mesajProtocol.text}
                {mesajProtocol.indemnContact && (
                  <>
                    {' '}
                    {mesajProtocol.indemnContact}{' '}
                    {TELEFON_HREF ? (
                      <a href={TELEFON_HREF} className={LINK}>
                        {CONTACT_ECOTIC.telefon}
                      </a>
                    ) : (
                      CONTACT_ECOTIC.telefon
                    )}
                    .
                  </>
                )}
              </p>
              {mesajProtocol.sePoateReincerca && (
                <Button type="button" variant="link" onClick={reincearca} className="min-h-11">
                  Încercați din nou
                </Button>
              )}
            </div>
          )}

          {mesajStareInlocuire && (
            <p role="status" aria-live="polite" className="text-small text-gray-600">
              {mesajStareInlocuire}
            </p>
          )}

          <p className="text-small">
            Pentru modificări ulterioare mergi în{' '}
            {caleProfil ? (
              <Link to={caleProfil} className={LINK}>
                Profil
              </Link>
            ) : (
              'Profil'
            )}
            .
          </p>
        </section>
      )}

      <ConfirmationDialog
        isOpen={confirmareInlocuire.isOpen}
        onClose={confirmareInlocuire.handleCancel}
        onConfirm={confirmareInlocuire.handleConfirm}
        title="Înlocuiești protocolul?"
        description="Documentul încărcat va înlocui protocolul de colaborare existent."
        confirmText="Înlocuiește"
        cancelText="Anulare"
        loading={inlocuireProtocolMutation.isPending}
      />

      <section data-acasa-film className="space-y-2">
        <h2 className="font-heading text-lg">Școala Ecoterrienilor — filmul de prezentare</h2>
        <YoutubeFacade videoId="tMM-Ea86pFs" titlu="Școala Ecoterrienilor — filmul de prezentare" />
      </section>

      <InParteneriatCu />

      <section data-acasa-contact className="space-y-1">
        <p>Persoana de contact</p>
        <p>{PERSOANA_CONTACT}</p>
        <p>
          {TELEFON_HREF ? (
            <a href={TELEFON_HREF} className={LINK}>
              {CONTACT_ECOTIC.telefon}
            </a>
          ) : (
            CONTACT_ECOTIC.telefon
          )}
        </p>
      </section>
    </div>
  );
}
